import {
  Component,
  AfterViewInit,
  ElementRef,
  ViewChild,
} from "@angular/core";
import { Chart, ChartConfiguration } from "chart.js/auto";
import moment from "moment";

import { DanpheChartsService } from "../../dashboards/shared/danphe-charts.service";
import { DLService } from "../../shared/dl.service";
import { CoreService } from "../../core/shared/core.service";
import { HomeBLServiceService } from "../Services/Home/home-blservice.service";
import { TranslateService } from "@ngx-translate/core";

import { HomeDashboardResponse } from "../Models/Home/HomeDashboardResponse";
import { ChartResponse } from "../Models/Home/ChartResponse";
import { MedicalStatisticsChartResponse } from "../Models/Home/MedicalStatisticsChartResponse";
import { PatientFlowStatusResponse } from "../Models/Home/PatientFlowStatusResponse";

@Component({
  selector: "my-app",
  templateUrl: "./dashboard-home.html",
  standalone: false,
})
export class DashboardHomeComponent implements AfterViewInit {

 

  constructor(
    public danpheCharts: DanpheChartsService,
    public dlService: DLService,
    public coreService: CoreService,
    public homeBLServiceService: HomeBLServiceService,
    private translate: TranslateService,
  ) {}
 //==================================================================Variables
 // ===================== Color Palette (Unified)
  private readonly chartColors = {
    teal: "rgba(0, 128, 128, 0.7)",
    purple: "rgba(140, 117, 178, 0.7)",
    navy: "rgba(26, 60, 81, 0.7)",
    green: "rgba(46, 204, 113, 0.7)",
    orange: "rgba(255, 159, 64, 0.7)",
    blue: "rgba(54, 162, 235, 0.7)",
    red: "rgba(231, 76, 60, 0.8)",
  };

  private readonly chartBorders = {
    teal: "rgba(0, 128, 128, 1)",
    purple: "rgba(140, 117, 178, 1)",
    navy: "rgba(26, 60, 81, 1)",
    green: "rgba(46, 204, 113, 1)",
    orange: "rgba(255, 159, 64, 1)",
    blue: "rgba(54, 162, 235, 1)",
    red: "rgba(231, 76, 60, 1)",
  };
  // ===================== Chart Instances
  zoneChart!: Chart;
  deptChart!: Chart;
  medicalChart!: Chart;

  // ===================== ViewChild
  @ViewChild("zoneChart") zoneChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild("deptChart") deptChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild("medicalChart") medicalChartRef!: ElementRef<HTMLCanvasElement>;

  // ===================== Data
  homeDashboardResponse!: HomeDashboardResponse;
  patientFlowStatusResponse!: PatientFlowStatusResponse;
  patientDistributionChartResponse!: ChartResponse[];
  visitDistributionChartResponse!: ChartResponse[];
  medicalStatisticsChartResponse!: MedicalStatisticsChartResponse[];

  currentDate = moment().format("DD-MM-YYYY");
  showCountryMap = true;

  //============================================== Methods
  ngOnInit(): void {
    this.loadHomeDashboardStatistics();
    this.loadPatientDistributionChart();
    this.loadVisitDistributionChart();
    this.loadMedicalStatisticsChart();
    this.loadPatientFlowStatus();
  }

  ngAfterViewInit(): void {
    this.showCountryMap = this.coreService.showCountryMapOnLandingPage;
  }

  private renderChart(
    canvas: ElementRef<HTMLCanvasElement>,
    chartInstance: Chart | null,
    config: ChartConfiguration,
  ): Chart {
    if (chartInstance) {
      chartInstance.destroy();
    }
    return new Chart(canvas.nativeElement, config);
  }

  loadHomeDashboardStatistics(): void {
    this.homeBLServiceService.getHomeDashboard().subscribe(res => {
      if (res.Succeeded) {
        this.homeDashboardResponse = res.Data;
      }
    });
  }

  loadPatientDistributionChart(): void {
    this.homeBLServiceService.getPatientDistributionChart().subscribe(res => {
      if (res.Succeeded && res.Data?.length) {
        this.patientDistributionChartResponse = res.Data;
        this.renderZoneChart();
      }
    });
  }

  loadVisitDistributionChart(): void {
    this.homeBLServiceService.getVisitDistributionChart().subscribe(res => {
      if (res.Succeeded && res.Data?.length) {
        this.visitDistributionChartResponse = res.Data;
        this.renderDeptChart();
      }
    });
  }

  loadMedicalStatisticsChart(): void {
    this.homeBLServiceService.getMedicalStatisticsChart().subscribe(res => {
      if (res.Succeeded && res.Data?.length) {
        this.medicalStatisticsChartResponse = res.Data;
        this.renderMedicalChart();
      }
    });
  }

  loadPatientFlowStatus(): void {
    this.homeBLServiceService.getPatientFlowStatus().subscribe(res => {
      if (res.Succeeded) {
        this.patientFlowStatusResponse = res.Data;
      }
    });
  }

  // ===================== Charts Rendering
  private renderZoneChart(): void {
    const labels = this.patientDistributionChartResponse.map(x => x.Name);
    const values = this.patientDistributionChartResponse.map(x => x.Count);

    this.zoneChart = this.renderChart(this.zoneChartRef, this.zoneChart, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: this.translate.instant("Home.Charts.Patients"),
            data: values,
            backgroundColor: [
              this.chartColors.teal,
              this.chartColors.purple,
              this.chartColors.navy,
              this.chartColors.green,
              this.chartColors.orange,
              this.chartColors.blue,
            ],
            borderColor: [
              this.chartBorders.teal,
              this.chartBorders.purple,
              this.chartBorders.navy,
              this.chartBorders.green,
              this.chartBorders.orange,
              this.chartBorders.blue,
            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true } },
        animation: { duration: 2000 },
      },
    });
  }

  private renderDeptChart(): void {
    const labels = this.visitDistributionChartResponse.map(x => x.Name);
    const values = this.visitDistributionChartResponse.map(x => x.Count);

    this.deptChart = this.renderChart(this.deptChartRef, this.deptChart, {
      type: "pie",
      data: {
        labels,
        datasets: [
          {
            label: this.translate.instant("Home.Charts.Appointments"),
            data: values,
            backgroundColor: [
              this.chartColors.red,
              this.chartColors.teal,
              this.chartColors.purple,
              this.chartColors.navy,
            ],
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "top" } },
        animation: { duration: 2000 },
      },
    });
  }

  private renderMedicalChart(): void {
    const sorted = [...this.medicalStatisticsChartResponse].sort((a, b) =>
      a.Year !== b.Year ? a.Year - b.Year : a.MonthNumber - b.MonthNumber
    );

    const labels = sorted.map(x => `${x.MonthName} ${x.Year}`);
    const values = sorted.map(x => x.Count);

    this.medicalChart = this.renderChart(
      this.medicalChartRef,
      this.medicalChart,
      {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: this.translate.instant("Home.Charts.PatientAdmissions"),
              data: values,
              borderColor: this.chartColors.red,
              backgroundColor: "rgba(231, 76, 60, 0.15)",
              fill: true,
              tension: 0.4,
              pointRadius: 5,
              pointHoverRadius: 7,
              pointHitRadius: 15,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "top" },
            tooltip: {
              callbacks: {
                label: ctx => `${this.translate.instant("Home.Charts.Admissions")}: ${ctx.raw}`,
              },
            },
          },
          scales: { y: { beginAtZero: true } },
          animation: { duration: 2000 },
        },
      }
    );
  }
}
